// frontend/packages/business-console/src/pages/Home.jsx
//
// Home, the operator's triage queue: "what needs me now" (design handoff
// Page 1). Deliberately NOT a metrics dashboard - the money / pipeline /
// visibility glance lives on Pulse, so the two surfaces don't duplicate
// each other. Home is a single focus-first column: a natural-language
// brief (what needs you + money at risk), the most urgent item lifted
// into a dark focus card, then a tight "Up next" list.
//
// Data: GET /v2/home/feed (ranked action items) + GET
// /v2/console/pulse/money (only for the at-risk/overdue triage headline).
// The feed degrades to an "all caught up" reward state. `?demo=1` (shared
// flag with Pulse) shows fixtures for marketing screenshots. Every row
// deep-links into its source section, where the actual action (remind,
// reply, send, convert) lives.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';

import { BusinessContext } from '../contexts/BusinessContext';
import { useAuth } from '../contexts/AuthContext';
import { useTokens, Surface, StatusPill } from '../components/primitives';
import { getHomeFeed } from '../services/homeFeedService';
import BuildProgressBanner from '../components/build-interview/BuildProgressBanner';
import { fetchMoneyPulse } from '../services/pulseService';
import { demoMoney } from './Pulse/demoData';
import useTenantFeatures from '../hooks/useTenantFeatures';
import { isDistrictMode } from '../components/layout/districtMode';

// ── Helpers ───────────────────────────────────────────────────────────

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function fmtMoney(cents) {
  if (typeof cents !== 'number' || Number.isNaN(cents)) return '$0';
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const secs = (d.getTime() - Date.now()) / 1000;
  const abs = Math.abs(secs);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (abs < 60) return 'just now';
  if (abs < 3600) return rtf.format(Math.round(secs / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(secs / 3600), 'hour');
  return rtf.format(Math.round(secs / 86400), 'day');
}

// Per feed-item type: the chip + the action button treatment. `chip` is
// a StatusPill kind; `btn` is one of filled-<color> / outline-<color>.
const TYPE_META = {
  'invoice-overdue': {
    Icon: ReceiptLongOutlinedIcon,
    chip: 'error',
    btn: 'filled-red',
  },
  'invoice-unpaid': {
    Icon: ReceiptLongOutlinedIcon,
    chip: 'warning',
    btn: 'outline-blue',
  },
  message: { Icon: InboxOutlinedIcon, chip: 'info', btn: 'filled-blue' },
  'quote-draft': {
    Icon: RequestQuoteOutlinedIcon,
    chip: 'neutral',
    btn: 'filled-blue',
  },
  'quote-accepted': {
    Icon: CheckCircleOutlineIcon,
    chip: 'success',
    btn: 'outline-green',
  },
  'lead-stale': {
    Icon: PersonOutlineIcon,
    chip: 'warning',
    btn: 'outline-blue',
  },
  'site-fix': {
    Icon: InsightsOutlinedIcon,
    chip: 'info',
    btn: 'outline-blue',
  },
};

// Short chip label per type (the backend's `actionLabel` drives the
// button text; this is the state badge).
const CHIP_LABEL = {
  'invoice-overdue': 'Overdue',
  'invoice-unpaid': 'Due',
  message: 'New',
  'quote-draft': 'Ready',
  'quote-accepted': 'Accepted',
  'lead-stale': 'Cold lead',
  'site-fix': 'Fix',
};

function readDemoFlag(searchString) {
  const params = new URLSearchParams(searchString || '');
  if (params.get('demo') === '1') return true;
  try {
    return localStorage.getItem('pulse.demo') === '1';
  } catch {
    return false;
  }
}

const DEMO_FEED = [
  {
    type: 'invoice-overdue',
    id: 'd1',
    href: 'invoices/d1',
    title: 'Oak trim removal',
    subtitle: 'R. Smith · Invoice #1042 · due Jun 5',
    amountCents: 45000,
    ts: new Date(Date.now() - 5 * 86400000).toISOString(),
    actionLabel: 'Remind',
  },
  {
    type: 'message',
    id: 'd2',
    href: 'inbox/d2',
    title: 'Dana Reyes',
    subtitle: '"Can you quote a maple removal out back?"',
    amountCents: null,
    ts: new Date(Date.now() - 4 * 60000).toISOString(),
    actionLabel: 'Reply',
  },
  {
    type: 'quote-draft',
    id: 'd3',
    href: 'quotes/d3',
    title: 'Maple removal estimate',
    subtitle: 'Alvarez',
    amountCents: 120000,
    ts: null,
    actionLabel: 'Send',
  },
  {
    type: 'quote-accepted',
    id: 'd4',
    href: 'quotes/d4',
    title: 'Hedge reshaping',
    subtitle: 'Chen',
    amountCents: 78000,
    ts: null,
    actionLabel: 'Convert',
  },
  {
    type: 'lead-stale',
    id: 'd6',
    href: 'crm/d6',
    title: 'Morgan Webb',
    subtitle: 'Lead · 18d no contact',
    amountCents: null,
    ts: new Date(Date.now() - 18 * 86400000).toISOString(),
    actionLabel: 'Follow up',
  },
  {
    type: 'invoice-unpaid',
    id: 'd5',
    href: 'invoices/d5',
    title: 'Stump grinding',
    subtitle: 'Patel · due in 2 days',
    amountCents: 32000,
    ts: null,
    actionLabel: 'Remind',
  },
  {
    type: 'site-fix',
    id: 'form-leak',
    href: 'lead-form',
    title: 'Your forms are losing visitors',
    subtitle: '23 started a form but did not finish',
    amountCents: null,
    ts: null,
    actionLabel: 'Review',
  },
];

// School district mode hides the sales stack, so its Home lists only the
// items that link to pages it keeps (messages, website fixes) and drops the
// money-at-risk brief.
const DISTRICT_FEED_TYPES = new Set(['message', 'site-fix']);

// ── Data hook ─────────────────────────────────────────────────────────

async function settled(promise) {
  try {
    return { ok: true, value: await promise };
  } catch (err) {
    return { ok: false, error: err };
  }
}

function useHomeData() {
  const location = useLocation();
  const isDemo = useMemo(
    () => readDemoFlag(location.search),
    [location.search]
  );

  const [state, setState] = useState({
    loading: !isDemo,
    feed: isDemo ? DEMO_FEED : [],
    feedError: null,
    // Money is kept only for the at-risk (overdue) triage headline; the
    // full money/pipeline/visibility glance lives on Pulse, not Home.
    money: isDemo ? demoMoney : null,
  });

  const load = useCallback(async () => {
    if (isDemo) {
      setState({
        loading: false,
        feed: DEMO_FEED,
        feedError: null,
        money: demoMoney,
      });
      return;
    }
    setState((s) => ({ ...s, loading: true, feedError: null }));
    const [feed, money] = await Promise.all([
      settled(getHomeFeed()),
      settled(fetchMoneyPulse()),
    ]);
    setState({
      loading: false,
      feed: feed.ok ? feed.value?.items || [] : [],
      feedError: feed.ok ? null : feed.error,
      money: money.ok ? money.value : null,
    });
  }, [isDemo]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await load();
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  return { ...state, isDemo, reload: load };
}

// ── Feed pieces ───────────────────────────────────────────────────────

function ActionButton({ item, onClick }) {
  const t = useTokens();
  const meta = TYPE_META[item.type] || { btn: 'outline-blue' };
  const label = item.actionLabel || 'Open';
  const styles = {
    'filled-red': {
      bgcolor: t.red,
      color: '#fff',
      border: 'none',
      hover: '#b91c1c',
    },
    'filled-blue': {
      bgcolor: t.blue,
      color: '#fff',
      border: 'none',
      hover: '#1d4ed8',
    },
    'outline-blue': {
      bgcolor: t.card,
      color: t.blue,
      border: `1px solid ${t.blueBorder}`,
      hover: t.blueBg,
    },
    'outline-green': {
      bgcolor: t.card,
      color: t.green,
      border: `1px solid ${t.greenBorder}`,
      hover: t.greenBg,
    },
  }[meta.btn];
  return (
    <Button
      onClick={(e) => {
        e.stopPropagation();
        onClick(item);
      }}
      sx={{
        flexShrink: 0,
        bgcolor: styles.bgcolor,
        color: styles.color,
        border: styles.border,
        fontWeight: 700,
        fontSize: 12.5,
        px: '13px',
        py: '7px',
        borderRadius: '8px',
        textTransform: 'none',
        '&:hover': { bgcolor: styles.hover },
      }}
    >
      {label}
    </Button>
  );
}

function FocusCard({ item, onOpen }) {
  const t = useTokens();
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const meta = TYPE_META[item.type] || {};
  const chipLabel =
    item.type === 'invoice-overdue' && item.ts
      ? `Overdue ${Math.max(1, Math.round((Date.now() - new Date(item.ts).getTime()) / 86400000))}d`
      : CHIP_LABEL[item.type] || 'Now';
  return (
    <Box
      onClick={() => onOpen(item)}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '16px',
        bgcolor: dark ? '#1e293b' : '#0f172a',
        color: '#fff',
        p: { xs: 2.25, md: 3 },
        cursor: 'pointer',
        border: dark ? `1px solid ${t.cardBorder}` : 'none',
        boxShadow: dark ? '0 10px 34px rgba(0,0,0,0.4)' : t.cardShadow,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: t.gradient,
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '.5px',
              textTransform: 'uppercase',
              color: '#fca5a5',
              bgcolor: 'rgba(220,38,38,.18)',
              px: '10px',
              py: '4px',
              borderRadius: '6px',
            }}
          >
            {chipLabel}
          </Box>
          <Typography
            sx={{
              fontSize: { xs: 18, md: 22 },
              fontWeight: 800,
              mt: '12px',
              mb: '4px',
            }}
          >
            {item.title}
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: '#94a3b8' }}>
            {item.subtitle}
          </Typography>
        </Box>
        {typeof item.amountCents === 'number' ? (
          <Typography
            sx={{
              fontSize: { xs: 24, md: 32 },
              fontWeight: 900,
              whiteSpace: 'nowrap',
            }}
          >
            {fmtMoney(item.amountCents)}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ mt: 2.5 }}>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onOpen(item);
          }}
          startIcon={meta.Icon ? <meta.Icon sx={{ fontSize: 17 }} /> : null}
          sx={{
            bgcolor: '#fff',
            color: '#0f172a',
            fontWeight: 800,
            fontSize: 13.5,
            px: '20px',
            py: '10px',
            borderRadius: '10px',
            textTransform: 'none',
            '&:hover': { bgcolor: '#e2e8f0' },
          }}
        >
          {item.actionLabel || 'Open'}
        </Button>
      </Box>
    </Box>
  );
}

function FeedRow({ item, onOpen }) {
  const t = useTokens();
  const meta = TYPE_META[item.type] || { chip: 'neutral' };
  return (
    <Surface
      interactive
      onClick={() => onOpen(item)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: '12px 14px',
        borderRadius: '12px',
      }}
    >
      <StatusPill
        size="sm"
        kind={meta.chip}
        label={CHIP_LABEL[item.type] || 'Now'}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontWeight: 700, fontSize: 13.5, color: t.text }}
        >
          {item.title}
        </Typography>
        <Typography noWrap sx={{ fontSize: 12, color: t.muted }}>
          {item.subtitle}
          {item.ts ? ` · ${relTime(item.ts)}` : ''}
        </Typography>
      </Box>
      {typeof item.amountCents === 'number' ? (
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: 14,
            color: t.text,
            whiteSpace: 'nowrap',
          }}
        >
          {fmtMoney(item.amountCents)}
        </Typography>
      ) : null}
      <ActionButton item={item} onClick={onOpen} />
    </Surface>
  );
}

function SectionLabel({ children }) {
  const t = useTokens();
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '1px',
        textTransform: 'uppercase',
        color: t.faint,
        mb: 1.25,
      }}
    >
      {children}
    </Typography>
  );
}

// ── Page ──────────────────────────────────────────────────────────────

export default function Home() {
  const t = useTokens();
  const navigate = useNavigate();
  const { business } = useContext(BusinessContext);
  const { user } = useAuth();
  const basePath = business?.slug ? `/business/${business.slug}` : '';
  const firstName = user?.firstName || '';

  const {
    loading,
    feed: rawFeed,
    feedError,
    money: rawMoney,
    isDemo,
    reload,
  } = useHomeData();
  const { features } = useTenantFeatures();
  const district = isDistrictMode(features);
  const feed = useMemo(
    () =>
      district
        ? rawFeed.filter((item) => DISTRICT_FEED_TYPES.has(item.type))
        : rawFeed,
    [district, rawFeed]
  );
  const money = district ? null : rawMoney;

  const open = useCallback(
    (item) => navigate(`${basePath}/${item.href}`),
    [navigate, basePath]
  );

  const overdueCents = money?.overdueCents || 0;

  // Natural-language brief. Home is the triage surface, so the brief is
  // about what needs you (count + money at risk), not vanity metrics
  // (earned/pipeline/visibility live on Pulse).
  const briefParts = [];
  if (feed.length > 0) {
    briefParts.push(
      `${feed.length} thing${feed.length === 1 ? '' : 's'} need you`
    );
  }
  if (overdueCents > 0) briefParts.push(`${fmtMoney(overdueCents)} at risk`);

  // The most urgent item lifts into the focus card when it is an overdue
  // invoice (money at risk); otherwise the whole list renders as rows.
  const focusItem = feed[0]?.type === 'invoice-overdue' ? feed[0] : null;
  const restItems = focusItem ? feed.slice(1) : feed;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2.5, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 3 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 900,
              letterSpacing: '-0.6px',
              color: t.text,
            }}
          >
            {greeting()}
            {firstName ? `, ${firstName}` : ''}
          </Typography>
          {briefParts.length > 0 ? (
            <Typography
              sx={{ fontSize: 13.5, color: t.muted, mt: 0.5, fontWeight: 500 }}
            >
              {briefParts.join(' · ')}
            </Typography>
          ) : null}
        </Box>
        {overdueCents > 0 ? (
          <Box sx={{ ml: 'auto', display: { xs: 'none', sm: 'block' } }}>
            <StatusPill
              kind="error"
              label={`${fmtMoney(overdueCents)} at risk`}
            />
          </Box>
        ) : null}
        {isDemo ? (
          <Box sx={{ ml: overdueCents > 0 ? 1 : 'auto' }}>
            <StatusPill kind="info" label="Demo data" />
          </Box>
        ) : null}
      </Box>

      {/* Guided re-entry into the build flow when a website build is in
          flight (renders nothing once the site is live or for operators with
          no build). */}
      <BuildProgressBanner />

      {/* Home is the triage queue ("what needs me now"); the money /
          pipeline / visibility metrics glance lives on Pulse. */}
      <Box sx={{ maxWidth: 760 }}>
        <Box>
          <SectionLabel>
            Needs you{feed.length ? ` · ${feed.length}` : ''}
          </SectionLabel>
          {feedError ? (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={reload}
                  sx={{ fontWeight: 700 }}
                >
                  Retry
                </Button>
              }
            >
              Could not load your feed.
            </Alert>
          ) : feed.length === 0 ? (
            <Surface sx={{ p: 5, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: t.text }}>
                You&rsquo;re all caught up
              </Typography>
              <Typography sx={{ fontSize: 13, color: t.muted, mt: 0.5 }}>
                Nothing needs you right now.
              </Typography>
            </Surface>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {focusItem ? (
                <>
                  <Box sx={{ mb: 0.25 }}>
                    <SectionLabel>Do this first</SectionLabel>
                  </Box>
                  <FocusCard item={focusItem} onOpen={open} />
                  {restItems.length ? (
                    <Box sx={{ mt: 1.5 }}>
                      <SectionLabel>Up next · {restItems.length}</SectionLabel>
                    </Box>
                  ) : null}
                </>
              ) : null}
              {restItems.map((item) => (
                <FeedRow
                  key={`${item.type}:${item.id}`}
                  item={item}
                  onOpen={open}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
}
